import { Modal } from "@peer-start/ui/reducate";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MentorSettings } from "../types/starting-teacher-mentor.js";
import type { StartingTeacherNoteSubject, StartingTeacherProgress } from "../data/starting-teacher-route.js";
import { StartingTeacherMentorCard } from "./StartingTeacherMentorCard.js";
import { StartingTeacherMentorModal } from "./StartingTeacherMentorModal.js";
import { StartingTeacherNoteModal } from "./StartingTeacherNoteModal.js";

function NotesModal({
  progress,
  open,
  onClose,
}: {
  progress: StartingTeacherProgress;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const coachNotes = progress.notes.filter((note) => note.text.trim());
  const topicNotes = Object.entries(progress.topics).filter(([, topic]) => topic.note.trim());
  const empty = coachNotes.length === 0 && topicNotes.length === 0;
  return (
    <Modal
      closeLabel={t("common.close")}
      controlId="starting-teacher.notes"
      onClose={onClose}
      open={open}
      title={t("startingTeacher.notesTitle")}
    >
      {empty ? (
        <p className="text-sm text-muted-foreground">{t("startingTeacher.notesEmpty")}</p>
      ) : (
        <ul className="space-y-4">
          {coachNotes.map((note) => (
            <li key={note.id}>
              <p className="text-sm font-medium">
                {t(
                  note.subjectKind === "competency"
                    ? "startingTeacher.noteSubjectCompetency"
                    : "startingTeacher.noteSubjectSkill",
                  {
                    title: t(
                      note.subjectKind === "competency"
                        ? `startingTeacher.competencies.${note.subjectId}.title`
                        : `startingTeacher.skills.${note.subjectId}.title`,
                    ),
                  },
                )}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{note.text}</p>
              {note.sharedWithCoach ? (
                <p className="mt-1 text-xs text-muted-foreground">{t("startingTeacher.noteShared")}</p>
              ) : null}
            </li>
          ))}
          {topicNotes.map(([topicId, topic]) => (
            <li key={topicId}>
              <p className="text-sm font-medium">{t(`startingTeacher.topics.${topicId}.title`)}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{topic.note}</p>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

export function StartingTeacherAside({
  progress,
  subject,
  onSaveMentor,
  onSaveNote,
}: {
  progress: StartingTeacherProgress;
  subject: StartingTeacherNoteSubject;
  onSaveMentor: (settings: MentorSettings) => void;
  onSaveNote: (subject: StartingTeacherNoteSubject, text: string, sharedWithCoach: boolean) => void;
}) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const openNotes = () => setNotesOpen(true);

  return (
    <>
      <StartingTeacherMentorCard
        onAddNote={() => setNoteOpen(true)}
        onChange={() => setSetupOpen(true)}
        onPrepare={openNotes}
        progress={progress}
      />
      <StartingTeacherNoteModal
        onClose={() => setNoteOpen(false)}
        onSave={(nextSubject, text, sharedWithCoach) => {
          onSaveNote(nextSubject, text, sharedWithCoach);
          setNoteOpen(false);
        }}
        open={noteOpen}
        subject={subject}
      />
      <StartingTeacherMentorModal
        onClose={() => setSetupOpen(false)}
        onSave={(settings) => {
          onSaveMentor(settings);
          setSetupOpen(false);
        }}
        open={setupOpen}
        progress={progress}
      />
      <NotesModal onClose={() => setNotesOpen(false)} open={notesOpen} progress={progress} />
    </>
  );
}
